import React, { useEffect, useMemo, useState, useRef } from 'react';
import { LockIcon, ChevronDownIcon, SearchIcon, XIcon } from 'lucide-react';
import { UserRole, Brand } from '../data/types';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
interface LoginPageProps {
  onLogin: (email: string, password: string, role: UserRole, brandName?: string, brandId?: string) => void;
  brands?: Brand[];
}
export function LoginPage({
  onLogin,
  brands = []
}: LoginPageProps) {
  const [activeTab, setActiveTab] = useState<'brand' | 'internal'>('brand');
  const [internalOrg, setInternalOrg] = useState<'admin' | 'grapevine'>('admin');
  const [selectedBrandId, setSelectedBrandId] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [brandSearch, setBrandSearch] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectedBrand = brands.find(b => b.id === selectedBrandId);
  // Filter and group brands by category
  const filteredBrands = useMemo(() => {
    const search = brandSearch.toLowerCase().trim();
    const filtered = search ? brands.filter(b => b.name.toLowerCase().includes(search) || b.category.toLowerCase().includes(search) || b.code && b.code.includes(search)) : brands;
    // Group by category
    const groups = new Map<string, Brand[]>();
    filtered.forEach(b => {
      if (!groups.has(b.category)) groups.set(b.category, []);
      groups.get(b.category)!.push(b);
    });
    return groups;
  }, [brands, brandSearch]);
  const totalFilteredCount = useMemo(() => Array.from(filteredBrands.values()).reduce((sum, arr) => sum + arr.length, 0), [filteredBrands]);
  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleBrandSelect = (brandId: string) => {
    setSelectedBrandId(brandId);
    const brand = brands.find(b => b.id === brandId);
    if (brand) {
      setEmail(brand.contactEmail);
      setBrandSearch('');
      setIsDropdownOpen(false);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (activeTab === 'brand' && !selectedBrandId) {
      setError('Please select your brand');
      return;
    }
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    setIsLoading(true);
    // Simulate brief auth delay for polish
    setTimeout(() => {
      const role: UserRole = activeTab === 'brand' ? 'brand' : internalOrg;
      onLogin(email, password, role, selectedBrand?.name, selectedBrand?.id);
      setIsLoading(false);
    }, 600);
  };
  return <div className="min-h-screen w-full bg-bg-warm flex items-center justify-center p-4" data-id="element-1958">
      <div className="w-full max-w-md" data-id="element-1959">
        {/* Logo */}
        <div className="text-center mb-10" data-id="element-1960">
          <img src="https://cdn.magicpatterns.com/uploads/4YqtTeUHiVXRLELZ1R2DtX/4dngqz0syxbaj28.png" alt="Woolworths" className="h-16 w-auto mx-auto mb-2" data-id="element-1961" />
          <p className="text-text-secondary text-sm font-medium tracking-wide" data-id="element-1962">
            Media Planner
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white border border-border p-8 rounded-lg shadow-sm" data-id="element-1963">
          {/* Tabs */}
          <div className="flex border-b border-border mb-8" data-id="element-1964">
            <button onClick={() => setActiveTab('brand')} className={`
                flex-1 pb-3 text-sm font-medium tracking-wide uppercase
                border-b-2 transition-colors duration-150
                ${activeTab === 'brand' ? 'border-primary text-primary' : 'border-transparent text-text-tertiary hover:text-text-secondary'}
              `} data-id="element-1965">
              Brand Login
            </button>
            <button onClick={() => setActiveTab('internal')} className={`
                flex-1 pb-3 text-sm font-medium tracking-wide uppercase
                border-b-2 transition-colors duration-150
                ${activeTab === 'internal' ? 'border-primary text-primary' : 'border-transparent text-text-tertiary hover:text-text-secondary'}
              `} data-id="element-1966">
              Internal Login
            </button>
          </div>

          {/* Internal Org Picker */}
          {activeTab === 'internal' && <div className="mb-6" data-id="element-1967">
              <label className="block text-xs font-semibold uppercase tracking-wide text-text-secondary mb-2.5" data-id="element-1968">
                Sign in as
              </label>
              <div className="grid grid-cols-2 gap-3" data-id="element-1969">
                <button type="button" onClick={() => setInternalOrg('admin')} className={`
                    flex items-center gap-2.5 px-4 py-3 rounded-md border text-left transition-all duration-150
                    ${internalOrg === 'admin' ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:border-text-tertiary'}
                  `} data-id="element-1970">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${internalOrg === 'admin' ? 'bg-primary/10' : 'bg-surface-warm'}`} data-id="element-1971">
                    <img src="https://cdn.magicpatterns.com/uploads/4YqtTeUHiVXRLELZ1R2DtX/4dngqz0syxbaj28.png" alt="Woolworths" className="w-6 h-6 object-contain" data-id="element-1972" />
                  </div>
                  <div data-id="element-1973">
                    <div className={`text-sm font-semibold ${internalOrg === 'admin' ? 'text-primary' : 'text-text-primary'}`} data-id="element-1974">
                      Woolworths
                    </div>
                    <div className="text-[11px] text-text-tertiary" data-id="element-1975">
                      Retailer
                    </div>
                  </div>
                </button>

                <button type="button" onClick={() => setInternalOrg('grapevine')} className={`
                    flex items-center gap-2.5 px-4 py-3 rounded-md border text-left transition-all duration-150
                    ${internalOrg === 'grapevine' ? 'border-gray-900 bg-gray-50 ring-1 ring-gray-900' : 'border-border hover:border-text-tertiary'}
                  `} data-id="element-1976">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${internalOrg === 'grapevine' ? 'bg-gray-100' : 'bg-surface-warm'}`} data-id="element-1977">
                    <img src="https://cdn.magicpatterns.com/uploads/p8LJ2RFGdVthnquHL1oesF/GV_Icon.png" alt="Grapevine" className="w-6 h-6 object-contain" data-id="element-1978" />
                  </div>
                  <div data-id="element-1979">
                    <div className={`text-sm font-semibold ${internalOrg === 'grapevine' ? 'text-gray-900' : 'text-text-primary'}`} data-id="element-1980">
                      Grapevine
                    </div>
                    <div className="text-[11px] text-text-tertiary" data-id="element-1981">
                      Platform
                    </div>
                  </div>
                </button>
              </div>
            </div>}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1982">
            {/* Brand Selector — Searchable */}
            {activeTab === 'brand' && <div ref={dropdownRef} data-id="element-1983">
                <label className="block text-xs font-semibold uppercase tracking-wide text-text-secondary mb-2.5" data-id="element-1984">
                  Select Your Brand
                </label>

                {/* Selected brand display OR search input */}
                {selectedBrandId && !isDropdownOpen ? <button type="button" onClick={() => {
              setIsDropdownOpen(true);
              setTimeout(() => searchInputRef.current?.focus(), 50);
            }} className="w-full flex items-center justify-between px-4 py-3 border border-primary bg-primary/5 rounded-md text-sm font-medium text-text-primary transition-all hover:bg-primary/10" data-id="element-1985">
                    <span data-id="element-1986">
                      {selectedBrand?.name}{' '}
                      <span className="text-text-tertiary font-normal" data-id="element-1987">
                        — {selectedBrand?.category}
                      </span>
                    </span>
                    <ChevronDownIcon className="w-4 h-4 text-text-tertiary" data-id="element-1988" />
                  </button> : <div className="relative" data-id="element-1989">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" data-id="element-1990" />
                    <input ref={searchInputRef} type="text" value={brandSearch} onChange={e => {
                setBrandSearch(e.target.value);
                setIsDropdownOpen(true);
              }} onFocus={() => setIsDropdownOpen(true)} placeholder="Search brands by name or category..." className="w-full pl-10 pr-10 py-3 border border-border rounded-md text-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary" data-id="element-1991" />
                    {brandSearch && <button type="button" onClick={() => {
                setBrandSearch('');
                searchInputRef.current?.focus();
              }} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary" data-id="element-1992">
                        <XIcon className="w-4 h-4" data-id="element-1993" />
                      </button>}
                  </div>}

                {/* Dropdown list */}
                {isDropdownOpen && <div className="mt-1 bg-white border border-border rounded-md shadow-lg max-h-64 overflow-y-auto z-50 relative" data-id="element-1994">
                    {totalFilteredCount === 0 ? <div className="px-4 py-6 text-center text-sm text-text-tertiary" data-id="element-1995">
                        No brands matching "{brandSearch}"
                      </div> : Array.from(filteredBrands.entries()).map(([category, categoryBrands]) => <div key={category} data-id="element-1996">
                            <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-text-tertiary bg-surface-warm sticky top-0" data-id="element-1997">
                              {category} ({categoryBrands.length})
                            </div>
                            {categoryBrands.map(brand => <button key={brand.id} type="button" onClick={() => handleBrandSelect(brand.id)} className={`w-full text-left px-4 py-2.5 text-sm hover:bg-primary/5 transition-colors flex items-center justify-between ${brand.id === selectedBrandId ? 'bg-primary/5 text-primary font-medium' : 'text-text-primary'}`} data-id="element-1998">
                                <span data-id="element-1999">{brand.name}</span>
                                {brand.code && <span className="text-[10px] text-text-tertiary font-mono" data-id="element-2000">
                                    {brand.code}
                                  </span>}
                              </button>)}
                          </div>)}
                  </div>}

                {selectedBrand && !isDropdownOpen && <p className="text-xs text-text-tertiary mt-1.5" data-id="element-2001">
                    Signing in as{' '}
                    <span className="font-medium text-text-secondary" data-id="element-2002">
                      {selectedBrand.name}
                    </span>{' '}
                    ({selectedBrand.contactEmail})
                  </p>}
              </div>}

            <Input label="Email Address" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" autoComplete="email" data-id="element-2003" />

            <div data-id="element-2004">
              <Input label="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" showPasswordToggle autoComplete="current-password" data-id="element-2005" />
              <div className="mt-2 text-right" data-id="element-2006">
                <button type="button" onClick={() => setError('Please contact your Woolworths or Grapevine administrator to reset your password.')} className="text-xs text-text-tertiary hover:text-text-secondary transition-colors" data-id="element-2007">
                  Forgot Password?
                </button>
              </div>
            </div>

            {error && <p className="text-sm text-urgent" data-id="element-2008">{error}</p>}

            <Button type="submit" variant="primary" size="lg" disabled={isLoading} className={`w-full mt-2 ${activeTab === 'internal' && internalOrg === 'grapevine' ? 'bg-gray-900 hover:bg-gray-800' : ''} ${isLoading ? 'opacity-80 cursor-wait' : ''}`} data-id="element-2009">
              {isLoading ? <span className="flex items-center justify-center gap-2" data-id="element-2010">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" data-id="element-2011">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-2012" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" data-id="element-2013" />
                  </svg>
                  Signing in...
                </span> : activeTab === 'brand' ? 'Sign In' : internalOrg === 'admin' ? 'Sign In to Woolworths' : 'Sign In to Grapevine'}
            </Button>
          </form>
        </div>

        {/* Security Note */}
        <div className="flex items-center justify-center gap-2 mt-6 text-text-tertiary" data-id="element-2014">
          <LockIcon className="w-3.5 h-3.5" data-id="element-2015" />
          <span className="text-xs" data-id="element-2016">Secure Enterprise Access</span>
        </div>
      </div>
    </div>;
}